import { Button, StatusLight } from '@react-spectrum/s2';
import { useCallback, useMemo } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import {
	CalendarGlyph,
	EmailThumbnail,
} from '@/components/artifact/ArtifactRow';
import { TYPE_TAGS } from '@/components/artifact/panelMeta';
import { BlobImage } from '@/components/blob/BlobImage';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { formatMetricValue } from '@/spaces/campaigns/MetricPills';
import {
	useCampaignMetrics,
	type MetricEntry,
} from '@/spaces/campaigns/useCampaigns';
import { getPanelIcon } from '@/spaces/chat/messages/panelTypeIcons';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import {
	OUTCOME_LABEL,
	OUTCOME_VARIANT,
} from '@/spaces/chat/panels/ownerActionOutcome.ts';
import { resolveProcessTitle } from '@/spaces/chat/panels/panelTitle';
import { MonitorRow } from '@/spaces/chat/panels/ProcessSchedulePanel';
import {
	rowThumbnailClass,
	scheduleItemTitle,
	statusToRowLabel,
} from '@/spaces/chat/panels/scheduleUtils';
import { useProcessActiveSwitch } from '@/spaces/chat/panels/useProcessActiveSwitch';
import { trackEvent } from '@/utils/fullstory';

import type {
	Action as ProcessAction,
	RequestOwnerActionConfig,
} from '../../../../../bridge/types/action.ts';
import type { Artifact } from '../../../../../bridge/types/artifact.ts';
import type { PanelProcess } from '../../../../../bridge/types/state.ts';
import {
	getContentArtifacts,
	getMonitorArtifacts,
	getOwnerActions,
	latestOwnerActionResponse,
} from '../../../../../bridge/utils/processClassification.ts';

interface ActiveCampaignPanelProps {
	panelProcess: PanelProcess;
	onClose: () => void;
	onBack?: () => void;
	style?: React.CSSProperties;
}

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<h3
		className="text-xs font-semibold tracking-widest text-(--text-disabled)
			uppercase"
	>
		{children}
	</h3>
);

type SummaryCell = {
	key: string;
	label: string;
	value: string;
	secondary?: string;
	hint?: string;
};

const formatPercentSecondary = (entry: MetricEntry): string | undefined => {
	if (entry.format !== 'percent' || !entry.raw) return undefined;
	const rawValue = Object.values(entry.raw)[0];
	if (typeof rawValue !== 'number') return undefined;
	return `(${rawValue})`;
};

const buildSummaryCells = (
	children: Artifact[],
	metrics: Record<string, MetricEntry> | undefined,
): SummaryCell[] => {
	const cells: SummaryCell[] = [];

	const scheduled = children.filter(
		(c) => (c as { status?: string }).status === 'scheduled',
	).length;
	const sent = children.filter(
		(c) => (c as { status?: string }).status === 'published',
	).length;

	cells.push({
		key: 'scheduled',
		label: 'Scheduled',
		value: String(scheduled),
	});
	cells.push({ key: 'sent', label: 'Sent', value: String(sent) });

	if (metrics) {
		for (const [key, entry] of Object.entries(metrics)) {
			if (entry.value == null) continue;
			cells.push({
				key,
				label: entry.label,
				value: formatMetricValue(entry),
				secondary: formatPercentSecondary(entry),
			});
		}
	}

	return cells;
};

const SummaryStripCell = ({ cell }: { cell: SummaryCell }) => (
	<div
		className="flex min-w-[110px] flex-1 flex-col justify-between gap-1 px-4
			first:pl-0"
	>
		<span
			className="text-[11px] font-medium tracking-[0.08em]
				text-(--text-disabled) uppercase"
		>
			{cell.label}
		</span>
		<div className="flex flex-col gap-1">
			<div className="flex items-baseline gap-1.5">
				<span
					className="tabular-nums"
					style={{
						fontSize: '26px',
						fontWeight: 600,
						color: 'var(--text-primary)',
						lineHeight: 1.1,
					}}
				>
					{cell.value}
				</span>
				{cell.secondary && (
					<span className="text-xs text-(--text-muted) tabular-nums">
						{cell.secondary}
					</span>
				)}
			</div>
			{cell.hint && (
				<span
					className="flex items-center gap-1.5 text-xs
						text-(--color-beacon-gold)"
				>
					<span className="size-1.5 rounded-full bg-(--color-beacon-gold)" />
					{cell.hint}
				</span>
			)}
		</div>
	</div>
);

const SummaryStrip = ({ cells }: { cells: SummaryCell[] }) => (
	<div
		className="flex items-stretch divide-x divide-(--glass-border)
			overflow-x-auto border-b border-(--glass-border) pb-5
			[-ms-overflow-style:none] [scrollbar-width:none]
			[&::-webkit-scrollbar]:hidden"
	>
		{cells.map((cell) => (
			<SummaryStripCell key={cell.key} cell={cell} />
		))}
	</div>
);

const ReadonlyThumbnail = ({ artifact }: { artifact: Artifact }) => {
	if (artifact.type === 'instagram_post') {
		const thumb = artifact.content.image_urls?.[0];
		return thumb ? (
			<BlobImage
				src={thumb}
				alt=""
				className={`${rowThumbnailClass} object-cover`}
			/>
		) : (
			<div className={`${rowThumbnailClass} bg-gray-100`} />
		);
	}
	if (artifact.type === 'email_html' || artifact.type === 'email_plaintext') {
		return <EmailThumbnail artifact={artifact} />;
	}
	return <div className={`${rowThumbnailClass} bg-gray-100`} />;
};

interface ReadonlyScheduleRowProps {
	artifact: Artifact;
	action: ProcessAction | null;
	processStatus: PanelProcess['status'];
	onPreview: () => void;
}

const ReadonlyScheduleRow = ({
	artifact,
	action,
	processStatus,
	onPreview,
}: ReadonlyScheduleRowProps) => {
	const title = scheduleItemTitle(artifact);
	const { label, muted, sent } = statusToRowLabel(
		artifact,
		action,
		processStatus,
	);

	return (
		<div
			className={`flex items-stretch gap-4 py-4 ${sent ? 'opacity-60' : ''}`}
		>
			{sent ? (
				<div className="shrink-0">
					<ReadonlyThumbnail artifact={artifact} />
				</div>
			) : (
				<button
					type="button"
					onClick={onPreview}
					className="shrink-0 transition-colors hover:opacity-80"
				>
					<ReadonlyThumbnail artifact={artifact} />
				</button>
			)}
			<div className="flex min-w-0 flex-1 flex-col gap-2 self-center">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div
					className={`flex items-center gap-2 text-base ${
						muted ? 'text-gray-500' : 'text-gray-700'
					}`}
				>
					<CalendarGlyph muted={muted} />
					<span>{label}</span>
				</div>
			</div>
		</div>
	);
};

const jobStatusLabel = (status: string): string => {
	if (status === 'active') return 'Active';
	if (status === 'paused') return 'Paused';
	return 'Draft';
};

// Work the owner does off-platform. Unlike a post or an email there is nothing
// to preview and no schedule to read — the row exists so the owner can get back
// to the ask after the campaign is live, which is when they actually do it.
const OwnerActionRow = ({
	action,
	skipped,
	onOpen,
}: {
	action: ProcessAction;
	skipped: boolean;
	onOpen: () => void;
}) => {
	const response = latestOwnerActionResponse(action);
	const answered = response !== null;
	const outcome = response?.outcome;
	const config = action.config as RequestOwnerActionConfig;

	let statusVariant: 'positive' | 'notice' | 'neutral' = 'notice';
	let statusLabel = 'Waiting on you';
	if (skipped) {
		statusVariant = 'neutral';
		statusLabel = 'Skipped';
	} else if (answered && outcome) {
		statusVariant = OUTCOME_VARIANT[outcome];
		statusLabel = OUTCOME_LABEL[outcome];
	}

	return (
		<button
			type="button"
			onClick={onOpen}
			data-testid="owner-action-row"
			data-ask-id={action.id}
			data-answered={answered ? 'true' : 'false'}
			className="flex items-stretch gap-4 py-4 text-left transition-colors
				hover:opacity-80"
		>
			<div
				className={`${rowThumbnailClass} flex shrink-0 items-center
					justify-center bg-gray-100`}
			>
				{getPanelIcon(action.type)}
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-2 self-center">
				<p className="line-clamp-2 text-lg font-bold text-gray-900">
					{config.instruction || 'Your part'}
				</p>
				<div className="flex items-center gap-2 text-base text-gray-700">
					<StatusLight variant={statusVariant} size="S">
						{statusLabel}
					</StatusLight>
				</div>
			</div>
		</button>
	);
};

// A monitor (ig_comment_watcher_class / subreddit_monitor) shown under "Jobs".
// Mirrors ReadonlyScheduleRow's icon + title layout, but monitors have no
// schedule — so it shows a read-only Active/Paused status line instead. The
// whole row opens the monitor's own panel (Pause/Resume live there).
const JobRow = ({
	artifact,
	onOpen,
}: {
	artifact: Artifact;
	onOpen: () => void;
}) => {
	// A monitor's saved summary sentence is the same text the standalone Jobs
	// widget shows (JobsWidget.tsx) — the type tag alone would read as generic
	// "Job" for every custom monitor embedded in a campaign, even though each
	// one watches something different.
	const title = artifact.user_summary || TYPE_TAGS[artifact.type] || 'Job';
	const status = (artifact as { status?: string }).status ?? '';
	const isActive = status === 'active';

	return (
		<button
			type="button"
			onClick={onOpen}
			className="flex items-stretch gap-4 py-4 text-left transition-colors
				hover:opacity-80"
		>
			<div
				className={`${rowThumbnailClass} flex shrink-0 items-center
					justify-center bg-gray-100`}
			>
				{getPanelIcon(artifact.type)}
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-2 self-center">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div className="flex items-center gap-2 text-base text-gray-700">
					<span
						className={`size-2 rounded-full ${
							isActive ? 'bg-green-500' : 'bg-gray-400'
						}`}
					/>
					<span>{jobStatusLabel(status)}</span>
				</div>
			</div>
		</button>
	);
};

export const ActiveCampaignPanel = ({
	panelProcess,
	onClose,
	onBack,
	style,
}: ActiveCampaignPanelProps) => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();
	const { metrics, metricsError } = useCampaignMetrics(panelProcess.id);

	const snapshot =
		bridgeApi.user_state?.process_setup_snapshots?.[panelProcess.id];
	const skippedActionIds = useMemo(
		() =>
			new Set(
				snapshot?.skipped_action_ids ?? panelProcess.skipped_action_ids ?? [],
			),
		[snapshot?.skipped_action_ids, panelProcess.skipped_action_ids],
	);

	// Skipped artifacts stay in the process's children (so they can be restored)
	// but were never activated — keep them out of the rendered campaign entirely,
	// including the summary strip's scheduled/sent counts.
	const children: Artifact[] = useMemo(() => {
		const skipped = new Set(panelProcess.skipped_artifact_ids ?? []);
		return (panelProcess.children ?? []).filter((c) => !skipped.has(c.id));
	}, [panelProcess.children, panelProcess.skipped_artifact_ids]);
	// Memoized because `panelProcess.actions ?? []` is a fresh array on every
	// render, which would defeat the memos below that depend on it.
	const actions: ProcessAction[] = useMemo(
		() => panelProcess.actions ?? [],
		[panelProcess.actions],
	);

	const emailActionsByRef = useMemo(() => {
		const map = new Map<string, ProcessAction>();
		for (const action of actions) {
			if (action.type !== 'send_email_to_contacts') continue;
			const ref = (action.config as { artifact_ref?: string })?.artifact_ref;
			if (ref) map.set(ref, action);
		}
		return map;
	}, [actions]);

	const { posts: postArtifacts, emails: emailArtifacts } = useMemo(
		() => getContentArtifacts(panelProcess),
		[panelProcess],
	);
	const ownerActions = useMemo(
		() => getOwnerActions(panelProcess),
		[panelProcess],
	);

	const { headerTagRender, actionFailureRender, pausedCaveatRender } =
		useProcessActiveSwitch({
			processId: panelProcess.id,
			status: panelProcess.status,
			updatedAt: panelProcess.updated_at,
			lastActionFailed: panelProcess.last_action_failed,
			pauseAriaLabel: 'Pause sending posts',
			resumeAriaLabel: 'Resume campaign',
		});

	const handleOpenArtifact = useCallback(
		(artifact: Artifact) => {
			trackEvent('Campaign Content Previewed', {
				process_id: panelProcess.id,
				artifact_id: artifact.id,
				artifact_type: artifact.type,
			});
			openChatPanel({
				panel_type: 'artifact',
				artifact_type: artifact.type,
				item_id: artifact.id,
			});
		},
		[openChatPanel, panelProcess.id],
	);

	const handleOpenAction = useCallback(
		(ownerAction: ProcessAction) => {
			trackEvent('Campaign Content Previewed', {
				process_id: panelProcess.id,
				artifact_id: ownerAction.id,
				artifact_type: ownerAction.type,
			});
			openChatPanel({
				panel_type: 'action',
				artifact_type: ownerAction.type,
				item_id: ownerAction.id,
			});
		},
		[openChatPanel, panelProcess.id],
	);

	const handleEditSchedule = useCallback(() => {
		trackEvent('Campaign Edit Schedule Clicked', {
			process_id: panelProcess.id,
		});
		openChatPanel({
			panel_type: 'process_schedule',
			item_id: panelProcess.id,
		});
	}, [openChatPanel, panelProcess.id]);

	const title = resolveProcessTitle(panelProcess, 'Campaign');
	const description = panelProcess.description?.trim() || null;

	const summaryCells = useMemo(
		() => buildSummaryCells(children, metrics),
		[children, metrics],
	);
	const showStrip = children.length > 0 || metrics !== undefined;

	const monitorArtifacts = useMemo(
		() => getMonitorArtifacts(panelProcess),
		[panelProcess],
	);

	// A monitor that owns an artifact renders under "Jobs" by name, so counting
	// its action here too double-rendered every comment watcher: once as a
	// generic MonitorRow in Content, and again as a named JobRow in Jobs.
	// `monitor_instagram_comments_and_reply` binds only `dm_artifact_refs`
	// though, so a campaign can carry the action with no watcher artifact at all.
	// Jobs names nothing for that shape, and without this row the owner sees no
	// sign that the automated DMs are running. That same missing `artifact_ref`
	// gives the action its own skippable setup row, so a declined one must not
	// report itself as ongoing here.
	const unnamedMonitorActions = useMemo(
		() =>
			monitorArtifacts.length > 0
				? []
				: actions.filter(
						(a) =>
							a.type === 'monitor_instagram_comments_and_reply' &&
							!skippedActionIds.has(a.id),
					),
		[actions, monitorArtifacts, skippedActionIds],
	);

	const hasSchedulableContent =
		postArtifacts.length > 0 ||
		emailArtifacts.length > 0 ||
		unnamedMonitorActions.length > 0;

	const hasEditableSchedule =
		postArtifacts.length > 0 || emailArtifacts.length > 0;

	const actionRenderer = hasEditableSchedule
		? () => (
				<div className="ml-auto">
					<Button type="button" variant="accent" onPress={handleEditSchedule}>
						Edit Schedule
					</Button>
				</div>
			)
		: undefined;

	return (
		<ChatPanelLayout
			title={title}
			headerTagRender={headerTagRender}
			actionRenderer={actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			<div className="flex flex-col gap-6 pt-4">
				{actionFailureRender()}
				{pausedCaveatRender()}
				{showStrip && <SummaryStrip cells={summaryCells} />}

				{/* Deliberately outside the showStrip guard. A results failure leaves metrics
				    undefined, so a campaign with no content renders no strip at all — nesting
				    this inside would hide the message in the case that needs it most. */}
				{metricsError && (
					<p className="text-sm text-(--text-muted)">
						Couldn&apos;t load results.
					</p>
				)}

				{description && (
					<p className="line-clamp-4 text-sm text-(--text-muted)">
						{description}
					</p>
				)}

				{/* An off-platform campaign has no Bridgeway-run content by design, so
				    "No content yet." would read as a failure rather than the truth.
				    Its work lives entirely in "Your part" below, or under "Jobs" when
				    the campaign is monitors only. */}
				{(hasSchedulableContent ||
					(ownerActions.length === 0 && monitorArtifacts.length === 0)) && (
					<section className="flex flex-col gap-3">
						<SectionHeading>Content</SectionHeading>
						{!hasSchedulableContent ? (
							<p className="text-sm text-(--text-disabled)">No content yet.</p>
						) : (
							<div className="flex flex-col divide-y divide-gray-200">
								{postArtifacts.map((artifact) => (
									<ReadonlyScheduleRow
										key={artifact.id}
										artifact={artifact}
										action={null}
										processStatus={panelProcess.status}
										onPreview={() => handleOpenArtifact(artifact)}
									/>
								))}
								{emailArtifacts.map((artifact) => {
									const action = artifact.ref
										? (emailActionsByRef.get(artifact.ref) ?? null)
										: null;
									return (
										<ReadonlyScheduleRow
											key={artifact.id}
											artifact={artifact}
											action={action}
											processStatus={panelProcess.status}
											onPreview={() => handleOpenArtifact(artifact)}
										/>
									);
								})}
								{unnamedMonitorActions.map((action) => (
									<MonitorRow key={action.id} />
								))}
							</div>
						)}
					</section>
				)}

				{ownerActions.length > 0 && (
					<section
						className="flex flex-col gap-3"
						data-testid="owner-action-section"
					>
						<SectionHeading>Your part</SectionHeading>
						<div className="flex flex-col divide-y divide-gray-200">
							{ownerActions.map((ownerAction) => (
								<OwnerActionRow
									key={ownerAction.id}
									action={ownerAction}
									skipped={skippedActionIds.has(ownerAction.id)}
									onOpen={() => handleOpenAction(ownerAction)}
								/>
							))}
						</div>
					</section>
				)}

				{monitorArtifacts.length > 0 && (
					<section className="flex flex-col gap-3">
						<SectionHeading>Jobs</SectionHeading>
						<div className="flex flex-col divide-y divide-gray-200">
							{monitorArtifacts.map((artifact) => (
								<JobRow
									key={artifact.id}
									artifact={artifact}
									onOpen={() => handleOpenArtifact(artifact)}
								/>
							))}
						</div>
					</section>
				)}
			</div>
		</ChatPanelLayout>
	);
};
